import {
  Blocks,
  BrainCircuit,
  Building2,
  Clapperboard,
  HeartPulse,
  Search,
  Shield,
  Sprout,
  Workflow,
  Zap,
  type LucideIcon,
} from "lucide-react";

import { routes } from "@/app/common/functions/routes/routes";

/**
 * The colours a section's entries may carry, each a name from this site's
 * palette (tokens.css maps `[data-tone]` to `--section`): the brass signal,
 * the moss primary, and the diagram scale's four roles. The header's panels
 * and the phone menu set them on an entry's tile; nothing else reads them.
 */
export type SectionTone =
  "signal" | "moss" | "agent" | "success" | "keyword" | "repair";

export type SectionNavItem = {
  title: string;
  href: string;
  description: string;
  /** The entry's mark, on its tile in the section's panel. */
  icon: LucideIcon;
  /** The tile's colour. */
  tone: SectionTone;
};

export const capabilityNavItems: readonly SectionNavItem[] = [
  {
    title: "Intelligent Systems",
    href: routes.capabilities.detail("intelligent-systems"),
    description:
      "Applied AI, retrieval-backed workflows, and governed automation.",
    icon: BrainCircuit,
    tone: "agent",
  },
  {
    title: "Data, Search & Knowledge",
    href: routes.capabilities.detail("data-search-knowledge"),
    description:
      "Hybrid retrieval, canonical models, and measurable search quality.",
    icon: Search,
    tone: "success",
  },
  {
    title: "Automation & Operations",
    href: routes.capabilities.detail("automation-operations"),
    description:
      "Workflow automation, exception handling, and operational visibility.",
    icon: Workflow,
    tone: "repair",
  },
  {
    title: "Software Architecture & Engineering",
    href: routes.capabilities.detail("software-architecture-engineering"),
    description:
      "Software architecture, delivery foundations, and systems built to evolve.",
    icon: Blocks,
    tone: "keyword",
  },
];

export const industryNavItems: readonly SectionNavItem[] = [
  {
    title: "Energy",
    href: routes.industries.detail("energy"),
    description:
      "Operational, information, and workflow systems for energy environments where safety and real-world constraints matter.",
    icon: Zap,
    tone: "repair",
  },
  {
    title: "Agriculture",
    href: routes.industries.detail("agriculture"),
    description:
      "Planning and decision-support tools for farming and greenhouse operations.",
    icon: Sprout,
    tone: "success",
  },
  {
    title: "Enterprise",
    href: routes.industries.detail("enterprise"),
    description:
      "Enterprise systems across data, search, compliance, and internal platforms.",
    icon: Building2,
    tone: "agent",
  },
  {
    title: "Healthcare",
    href: routes.industries.detail("healthcare"),
    description:
      "Operational and product systems for regulated healthcare workflows.",
    icon: HeartPulse,
    tone: "signal",
  },
  {
    title: "Military",
    href: routes.industries.detail("military"),
    description:
      "Technical data, maintenance, and readiness systems for controlled environments.",
    icon: Shield,
    tone: "moss",
  },
  {
    title: "Media/Entertainment",
    href: routes.industries.detail("media-entertainment"),
    description:
      "Content, asset, and production systems that keep creative work moving.",
    icon: Clapperboard,
    tone: "keyword",
  },
];
